import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export const TwoFactorAuth = ({ pendingToken, setupRequired, onSuccess }) => {
  const { t } = useI18n();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(setupRequired);
  const authHeader = { headers: { Authorization: `Bearer ${pendingToken}` } };

  useEffect(() => {
    if (!setupRequired) return;
    (async () => {
      try {
        const { data } = await api.post("/auth/2fa/setup", {}, authHeader);
        setSetup(data);
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = setupRequired ? "/auth/2fa/setup/confirm" : "/auth/2fa/verify";
      const { data } = await api.post(path, { code: code.trim() }, authHeader);
      onSuccess(data.token, data.user);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const copyKey = () => {
    navigator.clipboard?.writeText(setup.manual_key);
    toast.success("Setup key copied");
  };

  return (
    <div className="space-y-5" data-testid="twofa-panel">
      <div className="flex items-center gap-2 text-orange-400">
        <ShieldCheck className="w-5 h-5" />
        <h3 className="font-head text-lg text-white">
          {setupRequired ? "Set up two-factor authentication" : "Two-factor authentication"}
        </h3>
      </div>

      {setupRequired ? (
        <p className="text-sm text-gray-400">
          Scan this QR code in the <span className="text-white">Google Authenticator</span> app, then enter the 6-digit code to finish.
        </p>
      ) : (
        <p className="text-sm text-gray-400">
          Enter the 6-digit code from your <span className="text-white">Google Authenticator</span> app.
        </p>
      )}

      {setupRequired && (
        loading ? (
          <div className="text-sm text-gray-500" data-testid="twofa-loading">Loading setup…</div>
        ) : setup && (
          <div className="space-y-3">
            <div className="flex justify-center">
              <img src={setup.qr_data_uri} alt="Google Authenticator QR code" data-testid="twofa-qr" className="w-44 h-44 rounded-lg bg-pure p-2 border border-white/10" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Manual setup key</Label>
              <div className="flex items-center gap-2 mt-1">
                <code data-testid="twofa-manual-key" className="flex-1 text-xs break-all bg-white/5 border border-white/10 rounded-md px-3 py-2 text-gray-200 keep-ltr">{setup.manual_key}</code>
                <button type="button" onClick={copyKey} data-testid="twofa-copy-key" className="text-gray-400 hover:text-orange-400 p-2"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        )
      )}

      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Verification code</Label>
          <Input data-testid="twofa-code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="mt-1 tracking-[0.5em] text-center text-lg keep-ltr" placeholder="000000" />
        </div>
        <Button type="submit" disabled={busy || code.length !== 6 || (setupRequired && !setup)} data-testid="twofa-submit-button"
          className="w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold py-6">
          {busy ? "..." : (setupRequired ? "Verify & Enable" : "Verify")}
        </Button>
      </form>
    </div>
  );
};
